import { __ } from 'i18n/index';
import { isDoublesMatch } from 'react/match/rankedPanel/matchTeams';

// A doubles lobby the host hasn't started: who is in it and in what state,
// whether the host can start, and the step the panel and the status line show
// for it (kind 'lobby'). The server's rules for Start match
// (Team_Ladder_Match::start_match): a set needs 4 accepted players, 2 per
// team; friendlies need 2 accounts.
//
// match.players holds the accepted players (team_number null until they pick
// one); lobby.players everyone in the lobby chat, so a lobby player who isn't
// accepted has asked to join; lobby.invited were invited and haven't joined.

const TEAM_SIZE = 2;

function myId() {
	return typeof myUser === 'undefined' ? null : myUser?.id;
}

const sameId = (first, second) => String(first) === String(second);

export function isTeamLobby(match) {
	return isDoublesMatch(match) && !!match.expiration;
}

function rowsOf(match, host) {
	const accepted = match.players || {};
	const rows = [];
	const seen = new Set();
	const add = (player, state) => {
		if(!player || seen.has(String(player.id))) {
			return;
		}
		seen.add(String(player.id));
		const team = state === 'joined' ? accepted[player.id]?.match?.team_number || null : null;
		rows.push({ player, state, team, isHost: !!host && sameId(host.id, player.id), isMe: sameId(player.id, myId()) });
	};
	// Teams in order, players without one last.
	const teamOrder = (player) => accepted[player.id]?.match?.team_number || 3;
	const joined = Object.values(accepted).map((entry) => entry?.player).filter(Boolean)
		.sort((first, second) => teamOrder(first) - teamOrder(second));
	add(joined.find((player) => host && sameId(player.id, host.id)), 'joined');
	joined.forEach((player) => add(player, 'joined'));
	Object.values(match.lobby?.players || {}).forEach((player) => add(player, accepted[player?.id] ? 'joined' : 'pending'));
	Object.values(match.lobby?.invited || {}).forEach((player) => add(player, 'invited'));
	return rows;
}

function canStartOf(ranked, joinedRows) {
	if(!ranked) {
		return joinedRows.length >= 2;
	}
	const perTeam = (number) => joinedRows.filter((row) => row.team === number).length;
	return joinedRows.length === TEAM_SIZE * 2 && perTeam(1) === TEAM_SIZE && perTeam(2) === TEAM_SIZE;
}

function stepOf(lobby) {
	const { iAmHost, hostName, pending, canStart, ranked, myState } = lobby;
	const rule = ranked
		? __('Ranked doubles starts with 4 players, 2 per team.')
		: __('Friendlies start with 2 or more players.');
	if(!iAmHost) {
		if(myState === 'pending') {
			return { kind: 'lobby', turn: 'theirs', title: __('Waiting for %s to accept you', hostName), detail: __('You asked to join %s\'s lobby.', hostName) };
		}
		return {
			kind: 'lobby',
			turn: 'theirs',
			title: __('Waiting for %s to start', hostName),
			detail: ranked
				? __('Pick your team. %s starts once there are 4 players, 2 per team.', hostName)
				: __('Pick your team. %s starts when everyone is here.', hostName),
		};
	}
	if(pending) {
		return {
			kind: 'lobby',
			turn: 'mine',
			title: __('%s wants to join', pending.username),
			detail: __('Accept to add %s, then put everyone on a team.', pending.username) + ' ' + rule,
		};
	}
	if(canStart) {
		return {
			kind: 'lobby',
			turn: 'mine',
			title: __('Ready to start'),
			detail: ranked
				? __('4 players, 2 per team. Start once everyone is set up.')
				: rule + (lobby.extraLocal ? ' ' + __('Your extra local players count toward your team.') : ''),
		};
	}
	if(lobby.rows.some((row) => row.state === 'invited')) {
		return { kind: 'lobby', turn: 'theirs', title: __('Waiting for your invites'), detail: rule };
	}
	return { kind: 'lobby', turn: 'theirs', title: __('Waiting for players'), detail: rule + ' ' + __('Invite someone or wait for requests to join.') };
}

// Who becomes host if the host leaves. The server picks whoever has been on
// a team longest, which is normally the host's partner; the payload has no
// join times, so this names the host's teammate, else anyone else on a team.
// Null when nobody else is on a team: then leaving closes the lobby.
function successorOf(joinedRows) {
	const hostRow = joinedRows.find((row) => row.isHost);
	const others = joinedRows.filter((row) => !row.isHost && row.team);
	const teammate = hostRow?.team ? others.find((row) => row.team === hostRow.team) : null;
	const row = teammate || others[0] || null;
	return row ? row.player : null;
}

export default function describeLobby(match) {
	const host = match.player1 || null;
	const rows = rowsOf(match, host);
	const joinedRows = rows.filter((row) => row.state === 'joined');
	const ranked = Number(match.match_count) > 0;
	const extraLocal = Number(match.extra_local_players) || 0;
	const mine = rows.find((row) => row.isMe);
	const pendingRow = rows.find((row) => row.state === 'pending');
	const canStart = canStartOf(ranked, joinedRows);
	const lobby = {
		host,
		hostName: host ? host.username : '',
		iAmHost: !!host && sameId(host.id, myId()),
		ranked,
		rows,
		joinedCount: joinedRows.length + extraLocal,
		required: TEAM_SIZE * 2,
		extraLocal,
		pending: pendingRow ? pendingRow.player : null,
		successor: successorOf(joinedRows),
		canStart,
		startNote: canStart ? null : (ranked ? __('Needs 4 players, 2 per team') : __('Needs at least 2 players')),
		myState: mine ? mine.state : null,
	};
	lobby.step = stepOf(lobby);
	return lobby;
}
